// ------------------------------------------------------------
// Editor dock layout schema (persisted per user)
// ------------------------------------------------------------

export type EditorPanelId =
  | 'node-palette'
  | 'canvas-settings'
  | 'routes'
  | 'layout'
  | 'elements'
  | 'properties';

export type DockSide = 'left' | 'right';

export type BottomPanelTab = 'scenario' | 'gates' | 'results' | 'diagnostics';

export interface DockColumnLayout {
  id: string;
  panels: EditorPanelId[];
  panelSizes: number[];
}

export interface DockSideLayout {
  columns: DockColumnLayout[];
  columnSizes: number[];
}

export interface EditorLayoutV1 {
  version: 1;
  left: DockSideLayout;
  right: DockSideLayout;
  rootSizes: [number, number, number];
  centerSizes: [number, number];
  bottomOpen: boolean;
  bottomTab: BottomPanelTab;
  closedPanels: EditorPanelId[];
}

export const PANEL_LABELS: Record<EditorPanelId, string> = {
  'node-palette': 'Node palette',
  'canvas-settings': 'Canvas',
  routes: 'Routes',
  layout: 'Layout & scale',
  elements: 'Elements',
  properties: 'Properties',
};

export const ALL_PANEL_IDS: EditorPanelId[] = [
  'node-palette',
  'canvas-settings',
  'routes',
  'layout',
  'elements',
  'properties',
];

export const PANEL_DEFAULT_SIDE: Record<EditorPanelId, DockSide> = {
  'node-palette': 'left',
  'canvas-settings': 'left',
  routes: 'left',
  layout: 'left',
  elements: 'right',
  properties: 'right',
};

function createColumn(
  id: string,
  panels: EditorPanelId[],
  panelSizes?: number[],
): DockColumnLayout {
  const count = panels.length;
  const defaultSize = count > 0 ? 100 / count : 100;
  return {
    id,
    panels,
    panelSizes:
      panelSizes ??
      panels.map(() => defaultSize),
  };
}

export function createDefaultEditorLayout(): EditorLayoutV1 {
  return {
    version: 1,
    left: {
      columns: [
        createColumn('left-0', [
          'node-palette',
          'canvas-settings',
          'routes',
          'layout',
        ]),
      ],
      columnSizes: [100],
    },
    right: {
      columns: [createColumn('right-0', ['elements', 'properties'], [38, 62])],
      columnSizes: [100],
    },
    rootSizes: [14, 67, 19],
    centerSizes: [72, 28],
    bottomOpen: false,
    bottomTab: 'scenario',
    closedPanels: [],
  };
}

function normalizeSizes(sizes: number[], count: number): number[] {
  if (count <= 0) return [];
  if (sizes.length !== count) {
    const each = 100 / count;
    return Array.from({ length: count }, () => each);
  }
  const sum = sizes.reduce((a, b) => a + b, 0);
  if (sum <= 0) {
    const each = 100 / count;
    return Array.from({ length: count }, () => each);
  }
  return sizes.map((s) => (s / sum) * 100);
}

function normalizeColumn(column: DockColumnLayout): DockColumnLayout {
  return {
    ...column,
    panelSizes: normalizeSizes(column.panelSizes, column.panels.length),
  };
}

function normalizeSide(side: DockSideLayout): DockSideLayout {
  const columns = side.columns.map(normalizeColumn);
  return {
    columns,
    columnSizes: normalizeSizes(side.columnSizes, columns.length),
  };
}

function visiblePanelIds(layout: EditorLayoutV1): Set<EditorPanelId> {
  const visible = new Set<EditorPanelId>();
  for (const side of ['left', 'right'] as const) {
    for (const column of layout[side].columns) {
      for (const panelId of column.panels) {
        visible.add(panelId);
      }
    }
  }
  return visible;
}

function syncClosedPanels(layout: EditorLayoutV1): EditorPanelId[] {
  const visible = visiblePanelIds(layout);
  const known = new Set(ALL_PANEL_IDS);
  const closed = new Set<EditorPanelId>();
  for (const panelId of layout.closedPanels ?? []) {
    if (known.has(panelId) && !visible.has(panelId)) {
      closed.add(panelId);
    }
  }
  return ALL_PANEL_IDS.filter((panelId) => closed.has(panelId));
}

export function isPanelOpen(
  layout: EditorLayoutV1,
  panelId: EditorPanelId,
): boolean {
  return visiblePanelIds(layout).has(panelId);
}

export function normalizeEditorLayout(layout: EditorLayoutV1): EditorLayoutV1 {
  const normalized = {
    ...layout,
    version: 1 as const,
    left: normalizeSide(layout.left),
    right: normalizeSide(layout.right),
    rootSizes: layout.rootSizes,
    centerSizes: layout.centerSizes,
    closedPanels: syncClosedPanels(layout),
  };
  return normalized;
}

export function parseEditorLayout(raw: unknown): EditorLayoutV1 | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as Partial<EditorLayoutV1>;
  if (value.version !== 1) return null;
  if (!value.left?.columns || !value.right?.columns) return null;

  const known = new Set(ALL_PANEL_IDS);
  for (const side of [value.left, value.right]) {
    for (const col of side.columns) {
      if (!col.id || !Array.isArray(col.panels)) return null;
      if (!col.panels.every((p) => known.has(p))) return null;
    }
  }

  return normalizeEditorLayout({
    version: 1,
    left: value.left,
    right: value.right,
    rootSizes: value.rootSizes ?? [14, 67, 19],
    centerSizes: value.centerSizes ?? [72, 28],
    bottomOpen: value.bottomOpen ?? false,
    bottomTab: value.bottomTab ?? 'scenario',
    closedPanels: Array.isArray(value.closedPanels) ? value.closedPanels : [],
  });
}

export function migrateLegacyLocalLayout(): EditorLayoutV1 | null {
  try {
    const orderRaw = localStorage.getItem('sim-designer-panel-section-order');
    const ratioRaw = localStorage.getItem('sim-designer-panel-top-ratio');
    if (!orderRaw && !ratioRaw) return null;

    const layout = createDefaultEditorLayout();
    const order = orderRaw ? (JSON.parse(orderRaw) as string[]) : ['elements', 'properties'];
    const ratio = ratioRaw ? Number.parseFloat(ratioRaw) : 0.38;
    const topRatio = Number.isFinite(ratio)
      ? Math.min(0.82, Math.max(0.18, ratio))
      : 0.38;

    const panels: EditorPanelId[] =
      order[0] === 'properties'
        ? ['properties', 'elements']
        : ['elements', 'properties'];

    layout.right.columns = [
      createColumn('right-0', panels, [topRatio * 100, (1 - topRatio) * 100]),
    ];
    return layout;
  } catch {
    return null;
  }
}

function findPanelLocation(
  layout: EditorLayoutV1,
  panelId: EditorPanelId,
): { side: DockSide; columnIndex: number; panelIndex: number } | null {
  for (const side of ['left', 'right'] as const) {
    const columns = layout[side].columns;
    for (let ci = 0; ci < columns.length; ci += 1) {
      const pi = columns[ci].panels.indexOf(panelId);
      if (pi >= 0) return { side, columnIndex: ci, panelIndex: pi };
    }
  }
  return null;
}

export function movePanelInLayout(
  layout: EditorLayoutV1,
  panelId: EditorPanelId,
  targetSide: DockSide,
  targetColumnId: string | null,
  targetIndex: number,
): EditorLayoutV1 {
  const from = findPanelLocation(layout, panelId);
  if (!from) return layout;

  const next: EditorLayoutV1 = structuredClone(layout);

  let targetColumnIndex = targetColumnId
    ? next[targetSide].columns.findIndex((c) => c.id === targetColumnId)
    : -1;

  let creatingColumn = false;
  if (targetColumnIndex < 0) {
    creatingColumn = true;
    const newColumn = createColumn(`${targetSide}-${Date.now()}`, []);
    next[targetSide].columns.push(newColumn);
    next[targetSide].columnSizes.push(100);
    targetColumnIndex = next[targetSide].columns.length - 1;
  }

  const fromColumnIndex = from.columnIndex;
  const fromPanelIndex = from.panelIndex;
  const sameColumn =
    from.side === targetSide &&
    !creatingColumn &&
    fromColumnIndex === targetColumnIndex;

  const targetColumnBefore = next[targetSide].columns[targetColumnIndex];
  let insertAt = creatingColumn
    ? 0
    : Math.max(0, Math.min(targetIndex, targetColumnBefore.panels.length));

  const fromColumn = next[from.side].columns[fromColumnIndex];
  fromColumn.panels.splice(fromPanelIndex, 1);
  fromColumn.panelSizes.splice(fromPanelIndex, 1);

  if (fromColumn.panels.length === 0) {
    next[from.side].columns.splice(fromColumnIndex, 1);
    next[from.side].columnSizes.splice(fromColumnIndex, 1);
    if (
      from.side === targetSide &&
      !creatingColumn &&
      fromColumnIndex < targetColumnIndex
    ) {
      targetColumnIndex -= 1;
    }
  }

  const targetColumn = next[targetSide].columns[targetColumnIndex];
  if (!targetColumn) return normalizeEditorLayout(next);

  if (sameColumn && fromPanelIndex < insertAt) {
    insertAt -= 1;
  }
  insertAt = Math.max(0, Math.min(insertAt, targetColumn.panels.length));

  targetColumn.panels.splice(insertAt, 0, panelId);
  targetColumn.panelSizes.splice(
    insertAt,
    0,
    100 / Math.max(targetColumn.panels.length, 1),
  );

  return normalizeEditorLayout(next);
}

export function moveColumnToSide(
  layout: EditorLayoutV1,
  columnId: string,
  fromSide: DockSide,
  toSide: DockSide,
): EditorLayoutV1 {
  if (fromSide === toSide) return layout;
  const next: EditorLayoutV1 = structuredClone(layout);
  const fromIndex = next[fromSide].columns.findIndex((c) => c.id === columnId);
  if (fromIndex < 0) return layout;

  const [column] = next[fromSide].columns.splice(fromIndex, 1);
  const [size] = next[fromSide].columnSizes.splice(fromIndex, 1);
  column.id = `${toSide}-${Date.now()}`;
  next[toSide].columns.push(column);
  next[toSide].columnSizes.push(size ?? 100);

  return normalizeEditorLayout(next);
}

export function updateSideColumnSizes(
  layout: EditorLayoutV1,
  side: DockSide,
  sizes: number[],
): EditorLayoutV1 {
  const next: EditorLayoutV1 = structuredClone(layout);
  next[side].columnSizes = sizes;
  return normalizeEditorLayout(next);
}

export function updateColumnPanelSizes(
  layout: EditorLayoutV1,
  side: DockSide,
  columnId: string,
  sizes: number[],
): EditorLayoutV1 {
  const next: EditorLayoutV1 = structuredClone(layout);
  const column = next[side].columns.find((c) => c.id === columnId);
  if (!column) return layout;
  column.panelSizes = sizes;
  return normalizeEditorLayout(next);
}

export function closePanelInLayout(
  layout: EditorLayoutV1,
  panelId: EditorPanelId,
): EditorLayoutV1 {
  const from = findPanelLocation(layout, panelId);
  const next: EditorLayoutV1 = structuredClone(layout);
  next.closedPanels = syncClosedPanels(next);

  if (from) {
    const column = next[from.side].columns[from.columnIndex];
    column.panels.splice(from.panelIndex, 1);
    column.panelSizes.splice(from.panelIndex, 1);
    if (column.panels.length === 0) {
      next[from.side].columns.splice(from.columnIndex, 1);
      next[from.side].columnSizes.splice(from.columnIndex, 1);
    }
  }

  if (!next.closedPanels.includes(panelId)) {
    next.closedPanels = [...next.closedPanels, panelId];
  }

  return normalizeEditorLayout(next);
}

export function openPanelInLayout(
  layout: EditorLayoutV1,
  panelId: EditorPanelId,
): EditorLayoutV1 {
  if (isPanelOpen(layout, panelId)) return layout;

  const next: EditorLayoutV1 = structuredClone(layout);
  next.closedPanels = (next.closedPanels ?? []).filter((id) => id !== panelId);

  const side = PANEL_DEFAULT_SIDE[panelId];
  if (next[side].columns.length === 0) {
    next[side].columns.push(createColumn(`${side}-${Date.now()}`, [panelId]));
    next[side].columnSizes.push(100);
  } else {
    const column = next[side].columns[0];
    column.panels.push(panelId);
    column.panelSizes.push(100 / column.panels.length);
  }

  return normalizeEditorLayout(next);
}
